import { InfoCircleOutlined } from "@ant-design/icons";
import { message } from "antd";
import { isAxiosError } from "axios";
import React, { useMemo, useState } from "react";
import { useGetPaymentTypeOptions } from "../../../../../hooks/useGetPaymentTypeOptions";
import Grid from "../../../../atoms/Grid";
import { convertToTitleCase, formatCurrency, formatDateByType } from "../../../../libs/utils";
import { ButtonGroup, ButtonGroupProps } from "../../../../molecules/Button/ButtonGroup";
import { Invoice } from "../types";
import { RecallChequeRequest, useRecallChequeRequest, useUpdateRecallChequeRequest } from "./api";
import { RecallChequeForm } from "./RecallChequeForm";
import { ReplacementChequeForm } from "./ReplacementChequeForm";

type ReceiptRow = {
  subPdcId?: string;
  receiptNo: string | number;
  chequeNo: string | number;
  bankName: string;
  paidDate: string;
  amountToPay: number;
  status: string;
};

type RecallChequeProps = {
  invoice?: Invoice;
  actionType?: "apply" | "approveReject";
  onClose?: () => void;
  onSuccess?: () => void;
};

export type RecallSubmitParams = {
  recallType: RecallChequeRequest["recallType"];
  recallReason?: string;
  waiveOff?: boolean;
  modeOfPayment?: string;
  amountToPay?: number;
  documentNo?: string;
  bankName?: string;
  paidDate?: string;
  depositDate?: string;
  depositTo?: string;
  depositToBank?: string;
};

export const RecallCheque = (props: RecallChequeProps) => {
  const { invoice, actionType, onClose, onSuccess } = props;
  const usageType = invoice?.propertyDetails?.usagesType;
  const depositTo = invoice?.depositTo || invoice?.beneficiaryName;

  const isApprovalPending = actionType === "approveReject";

  const [step, setStep] = useState<1 | 2 | 3>(isApprovalPending ? 2 : 1);
  const [selectedReceipt, setSelectedReceipt] = useState<ReceiptRow | null>(
    isApprovalPending && invoice
      ? {
          receiptNo: invoice.receiptNo ?? "",
          chequeNo: invoice?.recallChequeRequest?.chequeNumber ?? invoice?.recallChequeRequest?.documentNo ?? "",
          bankName: invoice?.recallChequeRequest?.bankName ?? "",
          paidDate: invoice?.recallChequeRequest?.paidDate ?? "",
          amountToPay: invoice?.recallChequeRequest?.amountToPay ?? 0,
          status: "",
        }
      : null
  );

  const { paymentTypesOptions } = useGetPaymentTypeOptions({
    usageType: invoice?.propertyDetails?.usagesType,
    configKey: "masterPaymentTypeAttributesWithVat",
  });

  const recallFee = paymentTypesOptions.find((opt) => opt.value === "recall_fee")?.defaultAmount ?? 0;

  const onError = (error: unknown) => {
    if (isAxiosError(error)) message.error(error?.response?.data?.error);
  };

  const { recallChequeRequest, isPendingRecallChequeRequest } = useRecallChequeRequest({ onSuccess, onError });
  const { updateRecallChequeRequest, isPendingUpdateRecallChequeRequest } = useUpdateRecallChequeRequest({
    onSuccess,
    onError,
  });

  const handleApprove = async () => {
    if (!invoice?.pdcId) return;
    await updateRecallChequeRequest({ pdcId: invoice.pdcId, status: "approved" });
  };

  const handleReject = async () => {
    if (!invoice?.pdcId) return;
    await updateRecallChequeRequest({ pdcId: invoice.pdcId, status: "rejected" });
  };

  const isPartial = invoice?.subPdcs?.length > 0;

  const handleRecallSubmit = async (params: RecallSubmitParams) => {
    if (!invoice?.pdcId || !selectedReceipt) return;

    const payload: RecallChequeRequest = {
      pdcId: invoice.pdcId,
      chequeNumber: String(invoice.documentNo ?? ""),
      amountToPay: params.amountToPay,
      bankName: params.bankName,
      paidDate: params.paidDate,
      recallType: params.recallType,
      recallReason: params.recallReason,
      ...(params.waiveOff && { waiveOff: params.waiveOff }),
      ...(isPartial && {
        subPdcId: selectedReceipt.subPdcId,
        subChequeNumber: String(selectedReceipt.chequeNo),
      }),
      ...(params.recallType === "replaceWithNewReceipt" && {
        modeOfPayment: params.modeOfPayment,
        amountToPay: params.amountToPay,
        documentNo: params.documentNo,
        bankName: params.bankName,
        paidDate: params.paidDate,
        depositDate: params.depositDate,
        depositTo: params.depositTo,
        depositToBank: params.depositToBank,
      }),
    };

    await recallChequeRequest(payload);
  };

  const columns = [
    {
      field: "receiptNo",
      headerName: "Receipt No",
      flex: 1,
      maxWidth: 170,
      checkboxSelection: ({ data }) => !Boolean(data?.isCleared),
    },
    { field: "chequeNo", headerName: "Cheque No", flex: 1, maxWidth: 130 },
    {
      field: "bankName",
      headerName: "Bank",
      flex: 1,
      maxWidth: 220,
      valueFormatter: ({ value }) => convertToTitleCase(value),
    },
    {
      field: "paidDate",
      headerName: "Cheque Date",
      flex: 1,
      maxWidth: 140,
      valueFormatter: ({ value }) => formatDateByType(value),
    },
    {
      field: "amountToPay",
      headerName: "Amount",
      flex: 1,
      maxWidth: 170,
      type: "rightAligned",
      valueFormatter: ({ value }) => formatCurrency(value),
    },
    { field: "status", headerName: "Status", flex: 1, maxWidth: 130 },
  ];

  const getClearanceStatus = (isCleared?: boolean) => (isCleared ? "Cleared" : "Not Cleared");

  const data: ReceiptRow[] = useMemo(() => {
    if (!invoice) return [];

    const subPdcs = invoice?.subPdcs ?? [];

    const bankName = invoice?.bankName ?? "";

    if (subPdcs.length > 0) {
      return subPdcs.map((subPdc) => ({
        subPdcId: subPdc?.subPdcId,
        receiptNo: subPdc?.receiptNo ?? "",
        chequeNo: subPdc?.documentNo ?? "",
        bankName: bankName,
        paidDate: subPdc?.paidDate,
        amountToPay: subPdc?.amount,
        status: getClearanceStatus(subPdc?.isCleared),
      }));
    }

    return [
      {
        receiptNo: invoice?.receiptNo ?? "",
        chequeNo: invoice?.documentNo ?? "",
        bankName: bankName,
        paidDate: invoice?.paidDate,
        amountToPay: invoice?.totalAmount,
        status: getClearanceStatus(invoice?.isCleared),
      },
    ];
  }, [invoice]);

  const handleContinue = () => {
    if (!selectedReceipt) return;
    setStep(2);
  };

  const handleBack = () => {
    setStep(1);
    setSelectedReceipt(null);
  };

  const primaryButton: ButtonGroupProps["primaryButton"] = {
    label: "Continue",
    onClick: handleContinue,
    disabled: !selectedReceipt,
  };

  const secondaryButton: ButtonGroupProps["secondaryButton"] =
    step === 1 ? undefined : { label: "Back", onClick: onClose };

  if (step === 3 && selectedReceipt) {
    return (
      <ReplacementChequeForm
        selectedReceipt={selectedReceipt}
        usageType={usageType}
        depositTo={depositTo}
        recallFee={recallFee}
        onCancel={() => setStep(2)}
        onSubmit={handleRecallSubmit}
        isPending={isPendingRecallChequeRequest}
      />
    );
  }

  if (step === 2 && selectedReceipt) {
    return (
      <RecallChequeForm
        selectedReceipt={selectedReceipt}
        invoice={invoice}
        recallFee={recallFee}
        usageType={usageType}
        depositTo={depositTo}
        onCancel={isApprovalPending ? onClose : handleBack}
        onSubmit={handleRecallSubmit}
        onReplace={() => setStep(3)}
        onApprove={isApprovalPending ? handleApprove : undefined}
        onReject={isApprovalPending ? handleReject : undefined}
        isLoading={isPendingRecallChequeRequest || isPendingUpdateRecallChequeRequest}
        isApprovalPending={isApprovalPending}
      />
    );
  }

  return (
    <div>
      {(invoice?.subPdcs?.length ?? 0) > 1 && (
        <p style={{ margin: 0, paddingTop: 10 }}>This invoice has multiple receipts. Select a receipt to recall:</p>
      )}
      <Grid
        className="pl-0 pr-0"
        columns={columns}
        data={data}
        rowSelection="single"
        onSelectionChanged={(event) => {
          const rows = event.api.getSelectedRows() as ReceiptRow[];
          setSelectedReceipt(rows[0] ?? null);
        }}
      />
      <p style={{ display: "flex", alignItems: "center", gap: 6, color: "#999", fontStyle: "italic", fontSize: 13 }}>
        <InfoCircleOutlined />
        Cleared cheques cannot be recalled.
      </p>
      <ButtonGroup primaryButton={primaryButton} secondaryButton={secondaryButton} />
    </div>
  );
};
